import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FileText, Plus, Eye, CheckCircle, XCircle, Clock, Users, 
  ChevronRight, AlertTriangle, User, Phone, Mail, Calendar,
  Shield, Heart, Home, DollarSign, Building
} from 'lucide-react';

const INITIAL_FORM = {
  identity: { legal_name: '', preferred_name: '', date_of_birth: '', phone: '', email: '', emergency_contact: { name: '', phone: '', relationship: '' } },
  demographics: { gender: '', race_ethnicity: '', veteran_status: false, primary_language: 'English' },
  housing_status: { current_living_situation: '', expected_move_in_date: '', transportation_needs: '' },
  recovery_info: { primary_substance_history: '', date_of_last_use: '', currently_in_treatment: false, mat_participation: false, recovery_support_involvement: '' },
  mental_health: { diagnosed_conditions: '', current_medications: '', therapist_contact: '', hospitalization_history: '' },
  legal_supervision: { on_probation: false, on_parole: false, officer_name: '', officer_phone: '', officer_email: '', court_jurisdiction: '' },
  funding_source: { funding_type: '', insurance_info: '', other_details: '' },
  safety_screening: { history_of_violence: false, arson_history: false, sex_offense_status: false, medical_accommodations: '' },
  notes: '',
  preferred_property_id: '',
  referral_source_id: '',
};

export default function IntakeApplicationsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [newAppOpen, setNewAppOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [reviewForm, setReviewForm] = useState({ status: '', reviewer_notes: '', assigned_property_id: '', move_in_date: '' });
  const [properties, setProperties] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [appsRes, statsRes, propsRes] = await Promise.all([
        api.get('/intake/applications?entity=our_square'),
        api.get('/intake/stats?entity=our_square'),
        api.get('/housing/entity/our_square/houses'),
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
      setProperties(propsRes.data);
      try {
        const sourcesRes = await api.get('/referral-sources?entity=our_square');
        setReferralSources(sourcesRes.data);
      } catch (e) { console.log('No referral sources yet'); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submitApplication = async () => {
    if (!form.identity.legal_name || !form.identity.email) {
      return toast.error('Legal name and email are required');
    }
    try {
      await api.post('/intake/applications', { ...form, entity: 'our_square' });
      toast.success('Application submitted!');
      setNewAppOpen(false);
      setForm(INITIAL_FORM);
      load();
    } catch (e) { toast.error('Failed to submit'); }
  };

  const submitReview = async () => {
    if (!reviewForm.status) return toast.error('Select a decision');
    try {
      await api.put(`/intake/applications/${selectedApp.id}/review`, reviewForm);
      toast.success('Application reviewed!');
      setReviewOpen(false);
      setSelectedApp(null);
      setReviewForm({ status: '', reviewer_notes: '', assigned_property_id: '', move_in_date: '' });
      load();
    } catch (e) { toast.error('Failed to review'); }
  };

  const updateForm = (section, field, value) => {
    setForm(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const updateNestedForm = (section, nested, field, value) => {
    setForm(prev => ({ ...prev, [section]: { ...prev[section], [nested]: { ...prev[section][nested], [field]: value } } }));
  };

  const filteredApps = activeTab === 'all' ? applications : applications.filter(a => a.status === activeTab);

  const statusColors = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    under_review: 'bg-blue-50 text-blue-700 border-blue-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    denied: 'bg-red-50 text-red-700 border-red-200',
    waitlisted: 'bg-purple-50 text-purple-700 border-purple-200',
  };

  if (selectedApp && !reviewOpen) {
    return <ApplicationDetail app={selectedApp} onBack={() => setSelectedApp(null)} onReview={() => setReviewOpen(true)} />;
  }

  return (
    <div className="space-y-6" data-testid="intake-applications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Intake Applications</h1>
          <p className="text-sm text-muted-foreground mt-1">Review and manage housing applications</p>
        </div>
        <Dialog open={newAppOpen} onOpenChange={setNewAppOpen}>
          <DialogTrigger asChild>
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2" data-testid="new-application-btn">
              <Plus size={16} /> New Application
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl">
            <DialogHeader><DialogTitle>New Intake Application</DialogTitle></DialogHeader>
            <IntakeForm form={form} updateForm={updateForm} updateNestedForm={updateNestedForm} properties={properties} referralSources={referralSources} setForm={setForm} />
            <Button onClick={submitApplication} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full mt-4">Submit Application</Button>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total', value: stats.total || 0, icon: FileText, color: 'bg-slate-50 text-slate-700' },
          { label: 'Pending', value: stats.pending || 0, icon: Clock, color: 'bg-amber-50 text-amber-700' },
          { label: 'Under Review', value: stats.under_review || 0, icon: Eye, color: 'bg-blue-50 text-blue-700' },
          { label: 'Approved', value: stats.approved || 0, icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700' },
          { label: 'Denied', value: stats.denied || 0, icon: XCircle, color: 'bg-red-50 text-red-700' },
          { label: 'Waitlisted', value: stats.waitlisted || 0, icon: Users, color: 'bg-purple-50 text-purple-700' },
        ].map(s => (
          <Card key={s.label} className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mb-2`}><s.icon size={18} /></div>
              <p className="text-2xl font-bold text-slate-900">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="all" className="rounded-md text-xs">All ({applications.length})</TabsTrigger>
          <TabsTrigger value="pending" className="rounded-md text-xs">Pending ({stats.pending || 0})</TabsTrigger>
          <TabsTrigger value="approved" className="rounded-md text-xs">Approved ({stats.approved || 0})</TabsTrigger>
          <TabsTrigger value="waitlisted" className="rounded-md text-xs">Waitlisted ({stats.waitlisted || 0})</TabsTrigger>
          <TabsTrigger value="denied" className="rounded-md text-xs">Denied ({stats.denied || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <div className="space-y-2">
            {filteredApps.map(app => (
              <Card key={app.id} className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedApp(app)} data-testid={`app-${app.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold">
                      {app.applicant_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{app.applicant_name}</p>
                      <p className="text-xs text-muted-foreground">{app.applicant_email} • Applied {new Date(app.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${statusColors[app.status]} border`}>{app.status?.replace(/_/g, ' ')}</Badge>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredApps.length === 0 && (
              <Card className="border-0 shadow-sm rounded-xl">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No applications found</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Review Application</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">Reviewing application for: <strong>{selectedApp?.applicant_name}</strong></p>
            <div className="space-y-1.5">
              <Label>Decision</Label>
              <Select value={reviewForm.status} onValueChange={v => setReviewForm({ ...reviewForm, status: v })}>
                <SelectTrigger><SelectValue placeholder="Select decision" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approve</SelectItem>
                  <SelectItem value="waitlisted">Add to Waitlist</SelectItem>
                  <SelectItem value="denied">Deny</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reviewForm.status === 'approved' && (
              <>
                <div className="space-y-1.5">
                  <Label>Assign Property</Label>
                  <Select value={reviewForm.assigned_property_id} onValueChange={v => setReviewForm({ ...reviewForm, assigned_property_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                    <SelectContent>
                      {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.beds_available} available)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Move-in Date</Label>
                  <Input type="date" value={reviewForm.move_in_date} onChange={e => setReviewForm({ ...reviewForm, move_in_date: e.target.value })} />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <DictationInput value={reviewForm.reviewer_notes} onChange={e => setReviewForm({ ...reviewForm, reviewer_notes: e.target.value })} rows={3} showCleanup={false} />
            </div>
            <Button onClick={submitReview} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Submit Review</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IntakeForm({ form, updateForm, updateNestedForm, properties, referralSources, setForm }) {
  return (
    <div className="space-y-6 pt-4">
      {/* Identity */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><User size={14} /> Identity Information</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Legal Name *</Label><Input value={form.identity.legal_name} onChange={e => updateForm('identity', 'legal_name', e.target.value)} /></div>
          <div><Label>Preferred Name</Label><Input value={form.identity.preferred_name} onChange={e => updateForm('identity', 'preferred_name', e.target.value)} /></div>
          <div><Label>Date of Birth</Label><Input type="date" value={form.identity.date_of_birth} onChange={e => updateForm('identity', 'date_of_birth', e.target.value)} /></div>
          <div><Label>Phone</Label><Input value={form.identity.phone} onChange={e => updateForm('identity', 'phone', e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Email *</Label><Input type="email" value={form.identity.email} onChange={e => updateForm('identity', 'email', e.target.value)} /></div>
        </div>
        <div className="p-3 bg-slate-50 rounded-lg space-y-2">
          <p className="text-xs font-medium text-slate-600">Emergency Contact</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <Input placeholder="Name" value={form.identity.emergency_contact.name} onChange={e => updateNestedForm('identity', 'emergency_contact', 'name', e.target.value)} />
            <Input placeholder="Phone" value={form.identity.emergency_contact.phone} onChange={e => updateNestedForm('identity', 'emergency_contact', 'phone', e.target.value)} />
            <Input placeholder="Relationship" value={form.identity.emergency_contact.relationship} onChange={e => updateNestedForm('identity', 'emergency_contact', 'relationship', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Demographics */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Users size={14} /> Demographics</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Gender</Label>
            <Select value={form.demographics.gender} onValueChange={v => updateForm('demographics', 'gender', v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="non_binary">Non-Binary</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Race/Ethnicity</Label><Input value={form.demographics.race_ethnicity} onChange={e => updateForm('demographics', 'race_ethnicity', e.target.value)} /></div>
          <div><Label>Primary Language</Label><Input value={form.demographics.primary_language} onChange={e => updateForm('demographics', 'primary_language', e.target.value)} /></div>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" checked={form.demographics.veteran_status} onChange={e => updateForm('demographics', 'veteran_status', e.target.checked)} className="rounded" />
            <Label className="mb-0">Veteran</Label>
          </div>
        </div>
      </div>

      {/* Housing Status */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Home size={14} /> Housing Status</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Current Living Situation</Label>
            <Select value={form.housing_status.current_living_situation} onValueChange={v => updateForm('housing_status', 'current_living_situation', v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="homeless">Homeless</SelectItem>
                <SelectItem value="shelter">Shelter</SelectItem>
                <SelectItem value="treatment_facility">Treatment Facility</SelectItem>
                <SelectItem value="incarcerated">Incarcerated</SelectItem>
                <SelectItem value="staying_with_family">Staying with Family/Friends</SelectItem>
                <SelectItem value="renting">Renting</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Expected Move-in Date</Label><Input type="date" value={form.housing_status.expected_move_in_date} onChange={e => updateForm('housing_status', 'expected_move_in_date', e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Transportation Needs</Label><Input value={form.housing_status.transportation_needs} onChange={e => updateForm('housing_status', 'transportation_needs', e.target.value)} /></div>
        </div>
      </div>

      {/* Recovery Info */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Heart size={14} /> Recovery Information</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Primary Substance History</Label><DictationInput value={form.recovery_info.primary_substance_history} onChange={e => updateForm('recovery_info', 'primary_substance_history', e.target.value)} rows={2} showCleanup={false} /></div>
          <div><Label>Date of Last Use</Label><Input type="date" value={form.recovery_info.date_of_last_use} onChange={e => updateForm('recovery_info', 'date_of_last_use', e.target.value)} /></div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.recovery_info.currently_in_treatment} onChange={e => updateForm('recovery_info', 'currently_in_treatment', e.target.checked)} className="rounded" />
            <Label className="mb-0">Currently in Treatment</Label>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.recovery_info.mat_participation} onChange={e => updateForm('recovery_info', 'mat_participation', e.target.checked)} className="rounded" />
            <Label className="mb-0">MAT Participation</Label>
          </div>
          <div className="sm:col-span-2"><Label>Recovery Support Involvement</Label><Input value={form.recovery_info.recovery_support_involvement} onChange={e => updateForm('recovery_info', 'recovery_support_involvement', e.target.value)} /></div>
        </div>
      </div>

      {/* Mental Health */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><AlertTriangle size={14} /> Mental Health</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Diagnosed Conditions</Label><DictationInput value={form.mental_health.diagnosed_conditions} onChange={e => updateForm('mental_health', 'diagnosed_conditions', e.target.value)} rows={2} showCleanup={false} /></div>
          <div><Label>Current Medications</Label><DictationInput value={form.mental_health.current_medications} onChange={e => updateForm('mental_health', 'current_medications', e.target.value)} rows={2} showCleanup={false} /></div>
          <div><Label>Therapist/Psychiatrist Contact</Label><Input value={form.mental_health.therapist_contact} onChange={e => updateForm('mental_health', 'therapist_contact', e.target.value)} /></div>
          <div><Label>Hospitalization History</Label><Input value={form.mental_health.hospitalization_history} onChange={e => updateForm('mental_health', 'hospitalization_history', e.target.value)} /></div>
        </div>
      </div>

      {/* Legal Supervision */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Shield size={14} /> Legal Supervision</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.legal_supervision.on_probation} onChange={e => updateForm('legal_supervision', 'on_probation', e.target.checked)} className="rounded" /> On Probation</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.legal_supervision.on_parole} onChange={e => updateForm('legal_supervision', 'on_parole', e.target.checked)} className="rounded" /> On Parole</label>
          </div>
          <div><Label>Court Jurisdiction</Label><Input value={form.legal_supervision.court_jurisdiction} onChange={e => updateForm('legal_supervision', 'court_jurisdiction', e.target.value)} /></div>
          <div><Label>Officer Name</Label><Input value={form.legal_supervision.officer_name} onChange={e => updateForm('legal_supervision', 'officer_name', e.target.value)} /></div>
          <div><Label>Officer Phone</Label><Input value={form.legal_supervision.officer_phone} onChange={e => updateForm('legal_supervision', 'officer_phone', e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Officer Email</Label><Input value={form.legal_supervision.officer_email} onChange={e => updateForm('legal_supervision', 'officer_email', e.target.value)} /></div>
        </div>
      </div>

      {/* Funding Source */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><DollarSign size={14} /> Funding Source</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Funding Type</Label>
            <Select value={form.funding_source.funding_type} onValueChange={v => updateForm('funding_source', 'funding_type', v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="probation_voucher">Probation Voucher</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
                <SelectItem value="self_pay">Self-Pay</SelectItem>
                <SelectItem value="scholarship">Scholarship/Grant</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Insurance Info</Label><Input value={form.funding_source.insurance_info} onChange={e => updateForm('funding_source', 'insurance_info', e.target.value)} /></div>
          <div className="sm:col-span-2"><Label>Other Details</Label><Input value={form.funding_source.other_details} onChange={e => updateForm('funding_source', 'other_details', e.target.value)} /></div>
        </div>
      </div>

      {/* Safety Screening */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><AlertTriangle size={14} className="text-red-500" /> Safety Screening</h3>
        <div className="p-3 bg-red-50 rounded-lg border border-red-100 space-y-3">
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.safety_screening.history_of_violence} onChange={e => updateForm('safety_screening', 'history_of_violence', e.target.checked)} className="rounded" /> History of Violence</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.safety_screening.arson_history} onChange={e => updateForm('safety_screening', 'arson_history', e.target.checked)} className="rounded" /> Arson History</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.safety_screening.sex_offense_status} onChange={e => updateForm('safety_screening', 'sex_offense_status', e.target.checked)} className="rounded" /> Sex Offense Status</label>
          </div>
          <div><Label>Medical Accommodations Needed</Label><DictationInput value={form.safety_screening.medical_accommodations} onChange={e => updateForm('safety_screening', 'medical_accommodations', e.target.value)} rows={2} showCleanup={false} /></div>
        </div>
      </div>

      {/* Preferences */}
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Building size={14} /> Preferences</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Preferred Property</Label>
            <Select value={form.preferred_property_id || "none"} onValueChange={v => setForm(prev => ({ ...prev, preferred_property_id: v === "none" ? "" : v }))}>
              <SelectTrigger><SelectValue placeholder="No preference" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No preference</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Referral Source</Label>
            <Select value={form.referral_source_id || "none"} onValueChange={v => setForm(prev => ({ ...prev, referral_source_id: v === "none" ? "" : v }))}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {referralSources.map(s => <SelectItem key={s.id} value={s.id}>{s.organization_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Additional Notes</Label><DictationInput value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} showCleanup={false} /></div>
      </div>
    </div>
  );
}

function ApplicationDetail({ app, onBack, onReview }) {
  const sections = [
    { key: 'identity', title: 'Identity', icon: User },
    { key: 'demographics', title: 'Demographics', icon: Users },
    { key: 'housing_status', title: 'Housing Status', icon: Home },
    { key: 'recovery_info', title: 'Recovery Info', icon: Heart },
    { key: 'mental_health', title: 'Mental Health', icon: AlertTriangle },
    { key: 'legal_supervision', title: 'Legal Supervision', icon: Shield },
    { key: 'funding_source', title: 'Funding Source', icon: DollarSign },
    { key: 'safety_screening', title: 'Safety Screening', icon: AlertTriangle },
  ];

  const formatValue = (val) => {
    if (val === true) return 'Yes';
    if (val === false) return 'No';
    if (typeof val === 'object' && val !== null) {
      return Object.entries(val).filter(([k, v]) => v).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(', ') || '-';
    }
    return val || '-';
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-800',
    denied: 'bg-red-100 text-red-800',
    waitlisted: 'bg-purple-100 text-purple-800',
  };

  return (
    <div className="space-y-6" data-testid="application-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="rounded-full">&larr; Back</Button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{app.applicant_name}</h2>
            <p className="text-xs text-muted-foreground">{app.applicant_email} • Applied {new Date(app.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={statusColors[app.status]}>{app.status?.replace(/_/g, ' ')}</Badge>
          {app.status === 'pending' && <Button onClick={onReview} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Review Application</Button>}
        </div>
      </div>

      {app.reviewer_notes && (
        <Card className="border-l-4 border-l-cyan-500 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Review Notes by {app.reviewed_by_name}</p>
            <p className="text-sm">{app.reviewer_notes}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {sections.map(section => {
          const data = app[section.key] || {};
          const Icon = section.icon;
          return (
            <Card key={section.key} className="border-0 shadow-sm rounded-xl">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Icon size={14} /> {section.title}</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {Object.entries(data).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-right max-w-[60%]">{formatValue(value)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {app.notes && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Additional Notes</CardTitle></CardHeader>
          <CardContent><p className="text-sm">{app.notes}</p></CardContent>
        </Card>
      )}
    </div>
  );
}
